import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Activity, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function EdgeFunctionMonitor() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const agentFunctions = [
    'bridgette-command-router',
    'bridgette-get-daily-briefing',
    'bridgette-create-job',
    'bridgette-create-lead',
    'bridgette-schedule-appointment',
    'agent-memory-service',
    'agent-analytics',
    'agent-goals',
    'realtime-voice',
    'job-ai-assistant',
  ];

  const mockLogs = [
    {
      function: 'realtime-voice',
      timestamp: new Date().toISOString(),
      status: 'success',
      message: 'WebRTC session established successfully',
      duration_ms: 234
    },
    {
      function: 'bridgette-command-router',
      timestamp: new Date(Date.now() - 30000).toISOString(),
      status: 'success',
      message: 'Routed command: getDailyBriefing',
      duration_ms: 156
    },
    {
      function: 'agent-memory-service',
      timestamp: new Date(Date.now() - 60000).toISOString(),
      status: 'success',
      message: 'Retrieved preferences for org',
      duration_ms: 89
    },
  ];

  useEffect(() => {
    setLogs(mockLogs);
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLogs(mockLogs);
      setLoading(false);
    }, 500);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Edge Function Monitor
              </CardTitle>
              <CardDescription>
                Real-time monitoring of agent-related edge functions
              </CardDescription>
            </div>
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
            {agentFunctions.map((fn) => (
              <div key={fn} className="p-2 bg-muted/50 rounded text-center">
                <CheckCircle className="w-4 h-4 mx-auto mb-1 text-green-500" />
                <p className="text-xs font-mono truncate">{fn}</p>
              </div>
            ))}
          </div>

          <ScrollArea className="h-[400px] rounded-lg border p-4">
            <div className="space-y-3">
              {logs.map((log, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg"
                >
                  {log.status === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm font-semibold">
                        {log.function}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {log.duration_ms}ms
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{log.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(log.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
